// components/workstation-chat-cards.js
// ── Workstation status cards for the chat panel ──
//
// ParkedSummaryCard   — "Where you left off" gold card shown on workspace reopen.
// HealthReminderCard  — Health check sage card for stale nodes.
// HealthReminderRow   — One row inside HealthReminderCard.
// HealthAckButton     — Tiny action button (Revisit / Archive / Leave it).
// ChipsRow            — Clickable reply chips below a Shift message.
// CanvasSuggestionCard — Shift proposes a canvas build before acting.

import { useState } from 'react';

// ── Where you left off card ────────────────────────────────
// Pinned at the top of the chat when the workspace was parked. The
// card visually distinct from regular messages: gold left border, sage
// "PARKED" tag, italic body, two actions on the right.
//
// Continue keeps the card cleared and the user resumes work.
// Dismiss does the same — both call clearPark to wipe the metadata
// so the card doesn't reappear on the next reopen.
export function ParkedSummaryCard({ summary, parkedAt, onContinue, onDismiss }) {
  const relative = formatParkedRelative(parkedAt);
  return (
    <div
      style={{
        marginBottom: 18,
        padding: '12px 16px',
        background: 'rgba(200,169,110,0.04)',
        border: '1px solid rgba(200,169,110,0.18)',
        borderLeft: '2px solid #c8a96e',
        borderRadius: '4px 8px 8px 4px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        animation: 'wschat-park-in 360ms ease-out',
      }}
    >
      <style jsx global>{`
        @keyframes wschat-park-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 9,
          color: '#7a9e82',
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          marginBottom: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span>Where you left off</span>
          {relative && (
            <>
              <span style={{ color: '#2a2a2a' }}>·</span>
              <span style={{ color: '#555', textTransform: 'none', letterSpacing: 0, fontStyle: 'italic' }}>
                parked {relative}
              </span>
            </>
          )}
        </div>
        <div style={{
          fontSize: 13,
          lineHeight: 1.7,
          color: '#d4d4d4',
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
        }}>
          {summary}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
        <button
          onClick={onContinue}
          title="Continue from here"
          style={{
            background: 'rgba(200,169,110,0.08)',
            border: '1px solid rgba(200,169,110,0.3)',
            borderRadius: 4,
            color: '#c8a96e',
            fontSize: 10,
            fontFamily: 'Georgia, serif',
            padding: '4px 12px',
            cursor: 'pointer',
            letterSpacing: '0.04em',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(200,169,110,0.16)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(200,169,110,0.08)'; }}
        >
          Continue
        </button>
        <button
          onClick={onDismiss}
          title="Dismiss"
          style={{
            background: 'none',
            border: 'none',
            color: '#555',
            fontSize: 14,
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
            alignSelf: 'center',
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#c46050'}
          onMouseLeave={e => e.currentTarget.style.color = '#555'}
        >
          ×
        </button>
      </div>
    </div>
  );
}

// Format an ISO timestamp as a compact "X ago" string for the card.
export function formatParkedRelative(iso) {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  const diffWk = Math.floor(diffDay / 7);
  return `${diffWk}w ago`;
}

// ── Health reminder card ───────────────────────────────────
// Surfaces stale workspace nodes (>30 days old) on workspace open
// with three actions per row. Sage left border to differentiate
// from the gold "where you left off" card. Each row has a tight
// "Revisit / Archive / Leave it" trio that POSTs the user's choice
// to /api/workspace/health-acknowledge and clears that row.
export function HealthReminderCard({ nodes, onAck }) {
  return (
    <div
      style={{
        marginBottom: 18,
        padding: '12px 16px',
        background: 'rgba(122,158,130,0.04)',
        border: '1px solid rgba(122,158,130,0.18)',
        borderLeft: '2px solid #7a9e82',
        borderRadius: '4px 8px 8px 4px',
        animation: 'wschat-park-in 360ms ease-out',
      }}
    >
      <div style={{
        fontSize: 9,
        color: '#7a9e82',
        fontFamily: 'Georgia, serif',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        marginBottom: 10,
      }}>
        Health check · {nodes.length} {nodes.length === 1 ? 'item has' : 'items have'} been quiet
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {nodes.map(node => (
          <HealthReminderRow key={node.id} node={node} onAck={onAck} />
        ))}
      </div>
    </div>
  );
}

// One row inside the HealthReminderCard. Owns its own hover state
// for the action buttons.
function HealthReminderRow({ node, onAck }) {
  const typeLabel = node.type === 'file'
    ? (node.file_source === 'generated' ? 'File · Shift' : 'File · upload')
    : (node.type ? node.type.charAt(0).toUpperCase() + node.type.slice(1) : 'Node');
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      paddingBottom: 12,
      borderBottom: '1px solid rgba(122,158,130,0.08)',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 9,
          color: '#7a9e82',
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 3,
        }}>
          {typeLabel} · {node.age_days}d ago
        </div>
        <div style={{
          fontSize: 12,
          lineHeight: 1.5,
          color: '#d4d4d4',
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
          wordBreak: 'break-word',
        }}>
          {node.title}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginTop: 2 }}>
        <HealthAckButton label="Revisit" accent="#c8a96e" onClick={() => onAck(node, 'revisit')} />
        <HealthAckButton label="Archive" accent="#9b8fc4" onClick={() => onAck(node, 'archive')} />
        <HealthAckButton label="Leave it" accent="#666"   onClick={() => onAck(node, 'leave')} />
      </div>
    </div>
  );
}

// Tiny three-state action button. Same shape as the action buttons
// on the v2 grounded card so the surface feels consistent.
function HealthAckButton({ label, accent, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? `${accent}14` : 'none',
        border: `1px solid ${hovered ? `${accent}55` : '#2a2a2a'}`,
        borderRadius: 4,
        color: hovered ? accent : '#888',
        fontSize: 9,
        fontFamily: 'Georgia, serif',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '4px 9px',
        cursor: 'pointer',
        transition: 'all 0.12s',
        lineHeight: 1,
      }}
    >
      {label}
    </button>
  );
}

// ── Chips row — clickable reply choices after Shift asks a question ──
export function ChipsRow({ chips, onSelect }) {
  if (!chips || chips.length === 0) return null;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        fontSize: 9,
        color: '#555',
        fontFamily: 'Georgia, serif',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        marginBottom: 8,
      }}>
        Choose one
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 400 }}>
        {chips.map((chip, i) => (
          <button
            key={i}
            onClick={() => onSelect(chip)}
            style={{
              background: '#111',
              border: '1px solid #2a2a2a',
              borderRadius: 8,
              padding: '8px 14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              textAlign: 'left',
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(200,169,110,0.3)';
              e.currentTarget.style.background = '#141414';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#2a2a2a';
              e.currentTarget.style.background = '#111';
            }}
          >
            <span style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'rgba(200,169,110,0.1)',
              border: '1px solid rgba(200,169,110,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              color: '#c8a96e',
              fontFamily: 'Georgia, serif',
              flexShrink: 0,
            }}>
              {i + 1}
            </span>
            <span style={{
              fontSize: 13,
              color: '#d4d4d4',
              fontFamily: 'Georgia, serif',
              lineHeight: 1.4,
            }}>
              {chip}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Canvas suggestion card ─────────────────────────────────
// Shift proposes a canvas build before acting. Two actions:
// Approve (auto-sends a confirmation) or Dismiss.
export function CanvasSuggestionCard({ suggestion, aiName, onApprove, onDismiss }) {
  if (!suggestion) return null;
  return (
    <div style={{ marginBottom: 18, maxWidth: 400 }}>
      <div style={{
        background: 'rgba(200,169,110,0.03)',
        border: '1px solid rgba(200,169,110,0.15)',
        borderRadius: 6,
        padding: '6px 8px',
      }}>
        <div style={{ fontSize: 9, color: '#c8a96e', fontFamily: 'Georgia, serif', marginBottom: 3 }}>
          Canvas suggestion from {aiName || 'Shift'}
        </div>
        <div style={{ fontSize: 10, color: '#666', fontFamily: 'Georgia, serif', lineHeight: 1.45, marginBottom: 5 }}>
          &ldquo;{suggestion}&rdquo;
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <button
            onClick={onApprove}
            style={{ fontSize: 9, color: '#7a9e82', background: 'rgba(122,158,130,0.08)', border: '1px solid rgba(122,158,130,0.25)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: 'Georgia, serif' }}
          >
            Approve
          </button>
          <button
            onClick={onDismiss}
            style={{ fontSize: 9, color: '#333', background: '#111', border: '1px solid #1e1e2a', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: 'Georgia, serif' }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
